import {
	ADD_PRODUCT_BY_REF,
	ADD_PRODUCTS_BY_REF,
	GET_PROJECT,
	GET_CRM_ONLY_PRODUCTS,
	CREATE_PROJECT,
	EMPTY_PROJECT_DETAILS,
	CLONE_PRODUCT,
	VERSION_PROJECT,
	GET_PROJECT_VERSIONS,
	DELETE_PRODUCT,
	UPDATE_PROJECT,
	GET_PROJECT_TOOLTIP_INFO,
	UPDATE_PRODUCT,
	SET_PRODUCT_NAME,
	SET_COMPOSITION_NAME,
	SET_ARTICLE_NAME,
	UPDATE_PROJECT_THIRD_PARTY,
	SHARE_PROJECT
} from '@/redux/actions/project.actions'
import { LOGOUT } from '@/redux/actions/auth.actions'

const initialState = {
	loading: false,
	error: false,
	details: {},
	versions: [],
	creating: false,
	addingProduct: false,
	loadingCrmOnly: false,
	cloningProduct: false,
	versioningProduct: false,
	deletingProduct: false,
	updatingProduct: false,
	loadingTitleEditing: false,
	tooltipInfo: {
		loading: false,
		details: {}
	},
	requestBudget: {
		existing: null
	},
	sharedWith: []
}

const loading = (state = initialState.loading, action) => {
	switch (action.type) {
		case GET_PROJECT.REQUEST:
		case UPDATE_PROJECT.REQUEST:
			return !action.payload?.hotUpdate
		case GET_PROJECT.SUCCESS:
		case GET_PROJECT.FAILURE:
			return false
		default:
			return state
	}
}

const creating = (state = initialState.creating, action) => {
	switch (action.type) {
		case CREATE_PROJECT.REQUEST:
			return true
		case CREATE_PROJECT.SUCCESS:
		case CREATE_PROJECT.FAILURE:
			return false
		default:
			return state
	}
}

const error = (state = initialState.error, action) => {
	switch (action.type) {
		case GET_PROJECT.FAILURE:
			return true
		case GET_PROJECT.REQUEST:
		case GET_PROJECT.SUCCESS:
		case CLONE_PRODUCT.REQUEST:
		case CLONE_PRODUCT.SUCCESS:
		case VERSION_PROJECT.REQUEST:
		case VERSION_PROJECT.SUCCESS:
		case DELETE_PRODUCT.REQUEST:
		case DELETE_PRODUCT.SUCCESS:
		case UPDATE_PROJECT.REQUEST:
		case UPDATE_PROJECT.SUCCESS:
			return false
		default:
			return state
	}
}

const details = (state = initialState.details, action) => {
	switch (action.type) {
		case GET_PROJECT.REQUEST:
			return action.payload?.hotUpdate ? state : initialState.details

		case UPDATE_PROJECT.SUCCESS:
			return {
				...state,
				...Object.entries(action.payload).reduce(
					(acc, [key, value]) => ({
						...acc,
						[key]: value
					}),
					{}
				)
			}

		case GET_PROJECT.SUCCESS:
			return {
				...action.payload,
				products: action.payload.products?.reduce(
					(acc, product) => {
						return {
							orderedIds: [...acc.orderedIds, product._id],
							byId: { ...acc.byId, [product._id]: product },
							byType: {
								...acc.byType,
								[product.type]: acc.byType[product.type] ? [...acc.byType[product.type], product._id] : [product._id]
							}
						}
					},
					{ orderedIds: [], byId: {}, byType: {} }
				)
			}

		// PoC (showCrmOnlyProducts): merge the lazily-fetched CRM-only products
		// into the already-loaded project so they render as read-only ghost cards.
		case GET_CRM_ONLY_PRODUCTS.SUCCESS: {
			const ghosts = action.payload || []
			if (!state.products || ghosts.length === 0) return state
			const byId = { ...state.products.byId }
			const orderedIds = [...state.products.orderedIds]
			const byType = { ...state.products.byType }
			ghosts.forEach((product) => {
				if (byId[product._id]) return
				byId[product._id] = product
				orderedIds.push(product._id)
				byType[product.type] = byType[product.type] ? [...byType[product.type], product._id] : [product._id]
			})
			return { ...state, products: { byId, orderedIds, byType } }
		}

		case UPDATE_PROJECT_THIRD_PARTY:
			return { ...state, isAssignedThirdparty: action.payload || 0 }

		case EMPTY_PROJECT_DETAILS:
		case LOGOUT.SUCCESS:
		case LOGOUT.FAILURE:
			return {}
		case UPDATE_PRODUCT.SUCCESS: {
			const { productId, attributesToUpdate } = action.payload
			if (!state.products?.byId?.[productId]) return state
			return {
				...state,
				products: {
					...state.products,
					byId: {
						...state.products.byId,
						[productId]: {
							...state.products.byId[productId],
							...attributesToUpdate
						}
					}
				}
			}
		}
		case SET_PRODUCT_NAME.SUCCESS: {
			const { productId, name } = action.payload
			if (!state.products?.byId?.[productId]) return state
			return {
				...state,
				products: {
					...state.products,
					byId: {
						...state.products.byId,
						[productId]: { ...state.products.byId[productId], name }
					}
				}
			}
		}
		case SET_COMPOSITION_NAME.SUCCESS: {
			const { productId, compositionId, name } = action.payload
			if (!state.products?.byId?.[productId]) return state
			const product = state.products.byId[productId]
			if (!product.compositions) return state
			return {
				...state,
				products: {
					...state.products,
					byId: {
						...state.products.byId,
						[productId]: {
							...product,
							compositions: product.compositions.map(comp =>
								comp._id === compositionId ? { ...comp, name } : comp
							)
						}
					}
				}
			}
		}
		case SET_ARTICLE_NAME.SUCCESS: {
			const { productId, compositionId, articleId, name } = action.payload
			if (!state.products?.byId?.[productId]) return state
			const product = state.products.byId[productId]
			if (!product.compositions) return state
			return {
				...state,
				products: {
					...state.products,
					byId: {
						...state.products.byId,
						[productId]: {
							...product,
							compositions: product.compositions.map(comp =>
								comp._id === compositionId && comp.articles
									? {
										...comp,
										articles: comp.articles.map(art =>
											art._id === articleId ? { ...art, name } : art
										)
									}
									: comp
							)
						}
					}
				}
			}
		}
		case DELETE_PRODUCT.SUCCESS:
			return {
				...state,
				products: {
					orderedIds: state.products.orderedIds.filter((productId) => productId !== action.payload.productId),
					byId: Object.values(state.products.byId).reduce((acc, product) => {
						if (product._id === action.payload.productId) {
							return acc
						}

						return {
							...acc,
							[product._id]: product
						}
					}, {}),
					byType: Object.values(state.products.byId).reduce((acc, product) => {
						if (product._id === action.payload.productId) {
							return acc
						}

						return {
							...acc,
							[product.type]: product
						}
					}, {})
				}
			}
		default:
			return state
	}
}

const addingProduct = (state = initialState.addingProduct, action) => {
	switch (action.type) {
		case ADD_PRODUCT_BY_REF.REQUEST:
		case ADD_PRODUCTS_BY_REF.REQUEST:
			return true
		case ADD_PRODUCT_BY_REF.SUCCESS:
		case ADD_PRODUCTS_BY_REF.SUCCESS:
		case ADD_PRODUCT_BY_REF.FAILURE:
		case ADD_PRODUCTS_BY_REF.FAILURE:
			return false
		default:
			return state
	}
}

const loadingCrmOnly = (state = initialState.loadingCrmOnly, action) => {
	switch (action.type) {
		case GET_CRM_ONLY_PRODUCTS.REQUEST:
			return true
		case GET_CRM_ONLY_PRODUCTS.SUCCESS:
		case GET_CRM_ONLY_PRODUCTS.FAILURE:
			return false
		default:
			return state
	}
}

const cloningProduct = (state = initialState.cloningProduct, action) => {
	switch (action.type) {
		case CLONE_PRODUCT.REQUEST:
			return action.payload
		case CLONE_PRODUCT.SUCCESS:
		case CLONE_PRODUCT.FAILURE:
			return null
		default:
			return state
	}
}

const versioningProduct = (state = initialState.versioningProduct, action) => {
	switch (action.type) {
		case VERSION_PROJECT.REQUEST:
			return action.payload
		case VERSION_PROJECT.SUCCESS:
		case VERSION_PROJECT.FAILURE:
			return null
		default:
			return state
	}
}

const getProjectVersions = (state = initialState.versions, action) => {
	switch (action.type) {
		case GET_PROJECT_VERSIONS.SUCCESS:
			return action.payload
		case GET_PROJECT_VERSIONS.FAILURE:
			return initialState.versions
		case GET_PROJECT_VERSIONS.REQUEST:
		default:
			return state
	}
}

const deletingProduct = (state = initialState.deletingProduct, action) => {
	switch (action.type) {
		case DELETE_PRODUCT.REQUEST:
			return action.payload
		case DELETE_PRODUCT.SUCCESS:
		case DELETE_PRODUCT.FAILURE:
			return null
		default:
			return state
	}
}

const tooltipInfo = (state = initialState.tooltipInfo, action) => {
	switch (action.type) {
		case GET_PROJECT_TOOLTIP_INFO.REQUEST:
			return { ...state, loading: true }
		case GET_PROJECT_TOOLTIP_INFO.SUCCESS:
			return {
				details: action.payload,
				loading: false
			}
		case GET_PROJECT_TOOLTIP_INFO.FAILURE:
			return { ...state, loading: false }
		default:
			return state
	}
}

const updatingProduct = (state = initialState.updatingProduct, action) => {
	switch (action.type) {
		case UPDATE_PRODUCT.REQUEST:
		case SET_PRODUCT_NAME.REQUEST:
			return true
		case UPDATE_PRODUCT.SUCCESS:
		case UPDATE_PRODUCT.FAILURE:
		case SET_PRODUCT_NAME.SUCCESS:
		case SET_PRODUCT_NAME.FAILURE:
			return false
		default:
			return state
	}
}

const sharedWith = (state = initialState.sharedWith, action) => {
	switch (action.type) {
		case SHARE_PROJECT.SUCCESS:
			return [...state, action.payload]
		case GET_PROJECT.SUCCESS:
			return []
		case EMPTY_PROJECT_DETAILS:
			return []
		default:
			return state
	}
}

export default {
	name: 'project',
	reducers: {
		loading,
		error,
		creating,
		details,
		addingProduct,
		loadingCrmOnly,
		cloningProduct,
		versioningProduct,
		deletingProduct,
		tooltipInfo,
		updatingProduct,
		versions: getProjectVersions,
		sharedWith
	}
}
